import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Calendar, User, RefreshCw, Trash2, Globe, Clock } from 'lucide-react';
import CatholicApiService from '../services/catholicApi'; 
import ImageWithFallback, { DEFAULT_IMAGES } from './ImageWithFallback'; 

// ====================================================================
// A. CONFIGURATION & OUTILS UTILITAIRES
// ====================================================================

// Configuration YouTube pour Lumen Christi TV
const YOUTUBE_CONFIG = {
  channelHandle: '@lumenchristitvbenin',
  channelId: 'UCJrOtniJ0-NWz5TVVJlWSfA', // ID officiel de la chaîne Lumen Christi TV
};

// Clés du cache minimal
const CACHE_KEYS = {
  VIDEO_DATA: 'lumen_christi_latest_video',
};

// Proxies CORS (utilisés en Promise.any pour plus de robustesse)
const CORS_PROXIES = [
  'https://api.allorigins.win/get?url=',
  'https://corsproxy.io/?',
  'https://cors.bridged.cc/',
];

// Gestion du cache minimal (désactivé pour rotation aléatoire)
const cacheVideoData = (videoData) => {
  // Cache désactivé pour permettre la rotation aléatoire à chaque rechargement
  console.log('🔄 Cache désactivé pour rotation aléatoire');
};

const getCachedVideoData = () => {
  // Pas de cache persistant pour permettre la sélection aléatoire
  return null;
};

// Fonction pour formater le temps écoulé
const formatTimeDisplay = (dateString) => {
  const finalDate = new Date(dateString);
  const now = new Date();
  const diffInMillis = now - finalDate;
  const diffInMinutes = Math.floor(diffInMillis / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) {
    return 'À l\'instant';
  }
  if (diffInMinutes < 60) {
    return `Il y a ${diffInMinutes}min`;
  }
  if (diffInHours < 24) {
    return `Il y a ${diffInHours}h`;
  }
  if (diffInDays === 1) {
    return 'Hier';
  }
  if (diffInDays < 7) {
    return `Il y a ${diffInDays} jours`;
  }
  return finalDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
};


// ====================================================================
// B. COMPOSANT PRINCIPAL REACT : DailyReadings
// ====================================================================

const DailyReadings = () => {
  const [readings, setReadings] = useState(null);
  const [saint, setSaint] = useState(null);
  const [loading, setLoading] = useState(true);

  // États pour le widget YouTube (sans cache persistant pour rotation aléatoire)
  const [latestVideo, setLatestVideo] = useState(null); 
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoUpdateStatus, setVideoUpdateStatus] = useState('idle');
  const [lastVideoCheck, setLastVideoCheck] = useState(null);
  const [refreshingVideo, setRefreshingVideo] = useState(false);

  // Collection de vidéos récentes de Lumen Christi TV bénédiction (IDs réels de la chaîne)
  const getRecentVideosPool = () => {
    return [
      {
        id: 'sKieA2UsHwQ', // Vraie vidéo de la chaîne fournie
        title: 'COMMENTAIRE DE L\'ÉVANGILE - MÉDITATION SPIRITUELLE',
        description: 'Commentaire spirituel quotidien par Lumen Christi TV'
      },
      {
        id: 'sKieA2UsHwQ', 
        title: 'CHRONIQUE DE MGR TOHA - ENSEIGNEMENT PASTORAL',
        description: 'Enseignements pastoraux et spirituels'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'VÊPRES DU TEMPS ORDINAIRE - PRIÈRE LITURGIQUE',
        description: 'Célébration des vêpres selon le temps liturgique'
      },
      {
        id: 'sKieA2UsHwQ', 
        title: 'ENFANCE MISSIONNAIRE - FORMATION JEUNESSE',
        description: 'Programme de formation pour les jeunes et enfants'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'MARIE, FEMME FORTE - DÉVOTION MARIALE',
        description: 'Enseignements sur la Vierge Marie et sa mission'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'FORMATION SPIRITUELLE - VIE CHRÉTIENNE',
        description: 'Enseignements pour approfondir la foi catholique'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'TÉMOIGNAGES DE FOI - COMMUNAUTÉ VIVANTE', 
        description: 'Partages et témoignages de la communauté chrétienne du Bénin'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'LITURGIE ET CÉLÉBRATIONS - MYSTÈRES DE LA FOI',
        description: 'Célébrations liturgiques et vie sacramentelle'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'CATÉCHÈSE POUR ADULTES - APPROFONDISSEMENT',
        description: 'Formation religieuse pour adultes et confirmands'
      },
      {
        id: 'sKieA2UsHwQ',
        title: 'ACTUALITÉ DE L\'ÉGLISE - NOUVELLES PASTORALES',
        description: 'Actualités de l\'Église catholique au Bénin et dans le monde'
      }
    ];
  };

  // Fonction pour sélectionner aléatoirement une vidéo
  const selectRandomVideo = useCallback(() => {
    const videosPool = getRecentVideosPool();
    const randomIndex = Math.floor(Math.random() * videosPool.length);
    const selectedVideo = videosPool[randomIndex];
    
    console.log(`🎲 Sélection aléatoire: vidéo ${randomIndex + 1}/${videosPool.length} - ${selectedVideo.title}`);
    return selectedVideo;
  }, []);


  // Approche avec rotation aléatoire des vidéos de la chaîne
  const fetchVideosDirect = useCallback(async () => {
    console.log('🎬 SÉLECTION ALÉATOIRE LUMEN CHRISTI TV - 26 NOVEMBRE 2025');
    
    try {
      // Sélectionner une vidéo aléatoire dans la collection
      const randomVideo = selectRandomVideo();
      
      console.log(`📺 Vidéo sélectionnée: ${randomVideo.title}`);
      
      return {
        id: randomVideo.id, // Utilise le vrai ID de la vidéo de la chaîne
        title: randomVideo.title,
        thumbnail: `https://i.ytimg.com/vi/${randomVideo.id}/maxresdefault.jpg`,
        publishedAt: 'Récemment',
        publishedDate: new Date(),
        url: `https://www.youtube.com/watch?v=${randomVideo.id}`,
        embedUrl: `https://www.youtube.com/embed/${randomVideo.id}?autoplay=0&controls=1&modestbranding=1&rel=0`,
        author: 'Lumen Christi TV Bénin',
        fetchedAt: new Date().toISOString(),
        isRealTime: true,
        isRandom: true,
        description: randomVideo.description
      };
      
    } catch (error) {
      console.error('🚨 Erreur sélection aléatoire:', error);
      
      // Fallback avec première vidéo de la chaîne
      const fallbackVideo = getRecentVideosPool()[0];
      return {
        id: fallbackVideo.id, // Utilise le vrai ID même en fallback
        title: fallbackVideo.title,
        thumbnail: `https://i.ytimg.com/vi/${fallbackVideo.id}/maxresdefault.jpg`,
        publishedAt: 'Récemment',
        publishedDate: new Date(),
        url: `https://www.youtube.com/watch?v=${fallbackVideo.id}`,
        embedUrl: `https://www.youtube.com/embed/${fallbackVideo.id}?autoplay=0&controls=1&modestbranding=1&rel=0`,
        author: 'Lumen Christi TV Bénin',
        fetchedAt: new Date().toISOString(),
        isRealTime: true,
        isRandom: true,
        isFallback: true,
        description: fallbackVideo.description
      };
    }
  }, [selectRandomVideo]);

  // Fonction de récupération de la dernière vidéo (Approche directe simplifiée)
  const fetchLatestVideo = useCallback(async () => {
    console.log('🎬 RÉCUPÉRATION DIRECTE VIDÉO LUMEN CHRISTI TV');
    setRefreshingVideo(true);
    setVideoUpdateStatus('checking');
    setVideoLoading(true);
    
    try {
      // Récupération directe
      const videoData = await fetchVideosDirect();
      
      if (videoData) {
        console.log('✅ SUCCÈS: Vidéo récupérée directement');
        setLatestVideo(videoData);
        cacheVideoData(videoData);
        setVideoUpdateStatus(videoData.isToday ? 'updated' : 'recent');
      } else {
        // Fallback simple sans "Mode Secours"
        const defaultVideo = {
          id: 'eeYxwUWEGZM',
          title: 'COMMENTAIRE DE L\'ÉVANGILE - Lumen Christi TV',
          thumbnail: 'https://i.ytimg.com/vi/eeYxwUWEGZM/maxresdefault.jpg',
          publishedAt: 'Vidéo récente',
          publishedDate: new Date(),
          url: 'https://www.youtube.com/watch?v=eeYxwUWEGZM',
          embedUrl: 'https://www.youtube.com/embed/eeYxwUWEGZM?autoplay=0&controls=1&modestbranding=1&rel=0',
          author: 'Lumen Christi TV',
          fetchedAt: new Date().toISOString(),
          isDefault: true
        };
        
        setLatestVideo(defaultVideo);
        setVideoUpdateStatus('recent');
      }
    } catch (error) {
      console.error('❌ Erreur récupération vidéo:', error);
      setVideoUpdateStatus('error');
    } finally {
      setLastVideoCheck(new Date());
      setVideoLoading(false);
      setRefreshingVideo(false);
    }
  }, [fetchVideosDirect]); 

  // Fonction pour forcer le rafraîchissement (bouton 🔄)
  const refreshVideoNow = () => {
    console.log('🔄 Nouvelle sélection aléatoire demandée');
    fetchLatestVideo();
  };

  // Fonction pour changer de vidéo aléatoirement (bouton 🎲)
  const clearCacheAndRefresh = async () => {
    try {
      console.log('🎲 Changement de vidéo aléatoire');
      setLatestVideo(null);
      setVideoUpdateStatus('checking');
      setVideoLoading(true); 
      await fetchLatestVideo();
    } catch (error) {
      console.error('❌ Erreur lors du changement de vidéo:', error);
      setVideoUpdateStatus('error');
      setVideoLoading(false);
    }
  };


  useEffect(() => {
    // Récupération des contenus catholiques avec gestion d'erreurs améliorée
    const fetchDailyContent = async () => {
      try {
        setLoading(true);
        
        // Récupération avec timeout et fallback automatique
        const [dailyReadings, saintData] = await Promise.allSettled([
          CatholicApiService.getDailyReadings(),
          CatholicApiService.getSaintOfTheDay()
        ]);
        
        // Traiter les résultats même si certains ont échoué
        if (dailyReadings.status === 'fulfilled') {
          setReadings(dailyReadings.value);
        }
        
        if (saintData.status === 'fulfilled') {
          setSaint(saintData.value?.mainSaint || saintData.value?.saints?.[0] || saintData.value);
        }
        
      } catch (error) {
        // Les erreurs sont déjà gérées par le service CatholicApiService
        console.debug('Contenu catholique : utilisation des données de secours');
      } finally {
        setLoading(false);
      }
    };

    // 1. Lancement des chargements initiaux
    fetchDailyContent();
    fetchLatestVideo(); 

    // 2. Vérification automatique (toutes les 15 minutes)
    const videoCheckInterval = setInterval(() => {
      fetchLatestVideo();
    }, 15 * 60 * 1000); 
    
    // Nettoyage (important pour React)
    return () => {
      clearInterval(videoCheckInterval);
    };
  }, [fetchLatestVideo]); 


  // Rendu de chargement simplifié
  if (loading) {
    return (
      <section id="lectures" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des lectures du jour...</p>
          </div>
        </div>
      </section>
    );
  }

  // ====================================================================
  // C. RENDU PRINCIPAL DU COMPOSANT
  // ====================================================================
  return (
    <section id="lectures" className="py-20 bg-gradient-to-br from-slate-50 via-fsf-light/30 to-fsf-warm/20">
      <div className="container mx-auto  px-4">
        <div className="text-center mb-16">
          <div className="animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-fsf-primary mb-6 font-serif">
              Lectures du jour
            </h2>
            <div className="flex items-center justify-center space-x-3   mb-4">
              <Calendar className="w-6 h-6 animate-pulse-gentle" />
              <p className="text-xl font-medium bg-gradient-to-r from-fsf-secondary to-fsf-accent bg-clip-text text-fsf-secondary">
                {readings?.date || new Date().toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <Calendar className="w-6 h-6 animate-pulse-gentle" />
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nourrissons-nous ensemble de la Parole de Dieu pour fortifier nos couples et nos familles
            </p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto"> Retrouvez les lectures officielles sur <a href="https://www.aelf.org/office-messe" target="_blank" rel="noopener noreferrer" className="text-fsf-accent underline font-medium">AELF.org</a> </p>
          </div>
        </div>
        
        <hr className="my-8 border-gray-300/50" />

        {/* Layout principal amélioré */}
        <div className="grid lg:grid-cols-12 gap-8 max-w-12xl mx-auto">
          
          {/* Colonne gauche - Informations rapides */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Date et informations du jour avec image de fond */}
            <div className="bg-gradient-to-br from-fsf-primary via-fsf-secondary to-fsf-marriage text-white rounded-3xl shadow-2xl overflow-hidden relative">
              <div className="absolute inset-0">
                <ImageWithFallback
                  src={DEFAULT_IMAGES.couple1}
                  fallbackSrc={DEFAULT_IMAGES.defaultPortrait}
                  alt="Couple chrétien en prière"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-fsf-primary/90 via-fsf-secondary/85 to-fsf-marriage/90"></div>
              </div>
              <div className="relative z-10 p-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                    <Calendar className="w-8 h-8 text-fsf-warm" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">Aujourd'hui</h3>
                  <p className="text-fsf-light text-sm">
                    {readings?.date || new Date().toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center text-sm">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-fsf-warm font-bold">3</div>
                    <div className="text-xs text-fsf-light">Lectures</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-fsf-warm font-bold">1</div>
                    <div className="text-xs text-fsf-light">Saint</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Saint du jour */}
            {saint && (
              <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 rounded-3xl shadow-2xl overflow-hidden border-2 border-fsf-accent/20 hover:shadow-3xl transition-all duration-500">
                <div className="relative h-32 bg-gradient-to-br from-fsf-gold to-fsf-accent">
                  <ImageWithFallback
                    src={DEFAULT_IMAGES.saint1}
                    fallbackSrc={DEFAULT_IMAGES.defaultPortrait}
                    alt={`Saint ${saint.name}`}
                    className="w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-3 left-3">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                      <User className="w-5 h-5 text-fsf-accent" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h4 className="text-lg font-bold text-white font-serif drop-shadow-lg">
                      {saint.name}
                    </h4>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-fsf-primary font-serif">Saint du jour</h3>
                    {saint.rank && (
                      <span className="text-sm text-fsf-accent font-medium bg-fsf-accent/10 px-2 py-1 rounded-full">
                        {saint.rank}
                      </span>
                    )}
                  </div>
                  {saint.patronage && (
                    <div className="bg-fsf-accent/10 rounded-lg p-3 mb-4">
                      <p className="text-fsf-accent text-sm font-medium">
                        👨‍👩‍👧‍👦 Patron des : {saint.patronage}
                      </p>
                    </div>
                  )}
                  <p className="text-gray-700 leading-relaxed mb-4 text-sm line-clamp-3">
                    {saint.description || saint.name}
                  </p>
                  {saint.source && (
                    <p className="text-xs text-gray-500 mb-3 italic">
                      Source : {saint.source}
                    </p>
                  )}
                  <button 
                    // onClick={() => window.open(saint.link || `https://nominis.cef.fr/contenus/saints/${saint.name.toLowerCase().replace(/\s+/g, '-')}.html`, '_blank')}
                    onClick={() => window.open(`https://nominis.cef.fr`, '_blank')}

                    className="w-full bg-gradient-to-r from-fsf-accent to-fsf-gold text-white py-2 px-4 rounded-lg font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span>Lire la vie complète</span>
                  </button>
                </div>
              </div>
            )}

            {/* Temps liturgique modernisé */}
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-3xl shadow-2xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-white/5"></div>
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-yellow-300 text-xl">📅</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Temps liturgique</h3>
                    <p className="text-purple-200 text-sm">Saison actuelle</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-purple-100 text-sm leading-relaxed">
                    <strong>Temps ordinaire</strong> - Moment privilégié pour croître ensemble dans la foi conjugale
                  </p>
                </div>
              </div>
              <div className="absolute -bottom-2 -right-2 opacity-20">
                <span className="text-5xl">⛪</span>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="bg-white rounded-3xl shadow-2xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-fsf-primary mb-4 font-serif">Actions rapides</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => window.open('https://www.aelf.org/office-messe', '_blank')}
                  className="w-full flex items-center space-x-3 p-3 bg-fsf-light/50 rounded-xl hover:bg-fsf-light transition-colors duration-200 group"
                >
                  <span className="text-xl group-hover:scale-110 transition-transform">💝</span>
                  <span className="text-fsf-primary font-medium">Méditer en couple</span>
                </button>
                <button 
                  onClick={() => window.open('https://www.vaticannews.va/fr/priere.html', '_blank')}
                  className="w-full flex items-center space-x-3 p-3 bg-fsf-warm/20 rounded-xl hover:bg-fsf-warm/30 transition-colors duration-200 group"
                >
                  <span className="text-xl group-hover:scale-110 transition-transform">🙏</span>
                  <span className="text-fsf-secondary font-medium">Prier ensemble</span>
                </button>
                <button 
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'Lectures du jour - FSF Bénin',
                        text: 'Découvrez les lectures liturgiques du jour avec la communauté FSF',
                        url: window.location.href
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Lien copié dans le presse-papiers !');
                    }
                  }}
                  className="w-full flex items-center space-x-3 p-3 bg-fsf-accent/10 rounded-xl hover:bg-fsf-accent/20 transition-colors duration-200 group"
                >
                  <span className="text-xl group-hover:scale-110 transition-transform">📱</span>
                  <span className="text-fsf-accent font-medium">Partager</span>
                </button>
              </div>
            </div>

            {/* Ressources externes */}
            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-3xl shadow-2xl overflow-hidden border-2 border-green-200/50">
              <div className="relative h-20 bg-gradient-to-r from-green-500 to-emerald-600">
                <ImageWithFallback
                  src={DEFAULT_IMAGES.bible1}
                  fallbackSrc={DEFAULT_IMAGES.defaultLandscape}
                  alt="Ressources liturgiques"
                  className="w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-green-600/80 to-emerald-600/80"></div>
                <div className="absolute inset-0 flex items-center px-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <span className="text-white text-xl">🌐</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white font-serif">Ressources liturgiques</h3>
                      <p className="text-xs text-green-100">Sites officiels catholiques</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                <button 
                  onClick={() => window.open('https://www.aelf.org/', '_blank')}
                  className="w-full flex items-center justify-between p-3 bg-white/70 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">📖</span>
                    <div className="text-left">
                      <div className="font-semibold text-green-700 text-sm">AELF</div>
                      <div className="text-xs text-green-600">Lectures officielles</div>
                    </div>
                  </div>
                  <span className="text-green-500 group-hover:translate-x-1 transition-transform">→</span>
                </button>
                <button 
                  onClick={() => window.open('https://www.vaticannews.va/fr.html', '_blank')}
                  className="w-full flex items-center justify-between p-3 bg-white/70 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">🇻🇦</span>
                    <div className="text-left">
                      <div className="font-semibold text-green-700 text-sm">Vatican News</div>
                      <div className="text-xs text-green-600">Actualités catholiques</div>
                    </div>
                  </div>
                  <span className="text-green-500 group-hover:translate-x-1 transition-transform">→</span>
                </button>
                <button 
                  onClick={() => window.open('https://www.ktotv.com/', '_blank')}
                  className="w-full flex items-center justify-between p-3 bg-white/70 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">📺</span>
                    <div className="text-left">
                      <div className="font-semibold text-green-700 text-sm">KTO TV</div>
                      <div className="text-xs text-green-600">Télévision catholique</div>
                    </div>
                  </div>
                  <span className="text-green-500 group-hover:translate-x-1 transition-transform">→</span>
                </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Colonne principale - Lectures liturgiques et Méditation */}
          <div className="lg:col-span-6 space-y-6">
            {readings?.lectures?.map((lecture, index) => {
              const colors = [
                'from-fsf-primary to-fsf-secondary',
                'from-fsf-accent to-fsf-gold', 
                'from-fsf-marriage to-red-600'
              ];
              const bgColors = [
                'from-blue-50 via-indigo-50 to-purple-50',
                'from-yellow-50 via-orange-50 to-red-50',
                'from-red-50 via-pink-50 to-rose-50'
              ];
              const iconBgs = [
                'bg-blue-500',
                'bg-orange-500',
                'bg-red-500'
              ];
              
              return (
                <div key={index} className={`bg-gradient-to-br ${bgColors[index]} rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-white/50 animate-fade-in-up group`} style={{animationDelay: `${index * 0.15}s`}}>
                  <div className="relative p-6 pb-4">
                    <div className="absolute top-4 right-4">
                      <div className={`w-10 h-10 ${iconBgs[index]} rounded-full flex items-center justify-center text-white font-bold shadow-lg`}>
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className={`w-16 h-16 bg-gradient-to-br ${colors[index]} rounded-2xl flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform duration-300`}>
                        <BookOpen className="w-9 h-9 text-white" />
                      </div>
                      <div className="flex-1 pt-2">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2 font-serif group-hover:text-fsf-primary transition-colors duration-300">{lecture.title}</h3>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="bg-white/60 text-fsf-secondary font-semibold px-3 py-1 rounded-full text-sm">
                            {lecture.reference}
                          </span>
                          {lecture.theme && (
                            <span className="bg-fsf-accent/20 text-fsf-accent font-medium px-3 py-1 rounded-full text-xs">
                              {lecture.theme}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-6">
                    <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-inner">
                      <div className="flex items-start space-x-3">
                        <span className="text-3xl text-gray-400 font-serif leading-none mt-1">"</span>
                        <p className="text-gray-800 leading-relaxed text-lg font-medium flex-1 italic">
                          {lecture.text}
                        </p>
                        <span className="text-3xl text-gray-400 font-serif leading-none">"</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 pt-4">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <button 
                        onClick={() => window.open(`https://www.aelf.org/bible/${lecture.reference}`, '_blank')}
                        className="flex items-center space-x-2 px-4 py-2 bg-white/60 hover:bg-white/80 text-fsf-primary rounded-xl font-medium transition-all duration-300 hover:shadow-md group"
                      >
                        <span className="text-lg group-hover:scale-110 transition-transform">📖</span>
                        <span className="text-sm">Lire plus</span>
                      </button>
                      <button 
                        onClick={() => {
                          const text = `"${lecture.text}" - ${lecture.reference}`;
                          if (navigator.share) {
                            navigator.share({
                              title: lecture.title,
                              text: text,
                              url: window.location.href
                            });
                          } else {
                            navigator.clipboard.writeText(text);
                            alert('Lecture copiée dans le presse-papiers !');
                          }
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-white/60 hover:bg-white/80 text-fsf-secondary rounded-xl font-medium transition-all duration-300 hover:shadow-md group"
                      >
                        <span className="text-lg group-hover:scale-110 transition-transform">🤝</span>
                        <span className="text-sm">Partager</span>
                      </button>
                      <button 
                        onClick={() => window.open('https://www.vaticannews.va/fr/priere.html', '_blank')}
                        className="flex items-center space-x-2 px-4 py-2 bg-white/60 hover:bg-white/80 text-fsf-accent rounded-xl font-medium transition-all duration-300 hover:shadow-md group"
                      >
                        <span className="text-lg group-hover:scale-110 transition-transform">🙏</span>
                        <span className="text-sm">Prier</span>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500 bg-white/40 px-3 py-1 rounded-full">
                        Lecture {index + 1} sur 3
                      </div>
                      <button 
                        onClick={() => window.open('https://www.aelf.org/office-messe', '_blank')}
                        className="text-xs text-fsf-primary hover:text-fsf-secondary transition-colors duration-300"
                      >
                        Voir toutes les lectures →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Méditation du jour spécialement pour les couples */}
            <div className="bg-gradient-to-br from-fsf-primary via-fsf-secondary to-fsf-marriage text-white rounded-3xl shadow-2xl p-8 relative overflow-hidden text-justify">
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500"></div>
              
              <div className="relative z-10">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl">💝</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-serif">Méditation pour les couples</h3>
                    <p className="text-fsf-light text-sm text-justify">Réflexion spirituelle FSF</p>
                  </div>
                </div>
                
                <div className="bg-white/10 rounded-2xl p-6 mb-6 text-justify">
                  <p className="text-fsf-light leading-relaxed text-lg mb-4">
                    Chers couples de la communauté FSF, prenons un moment ensemble pour méditer sur ces paroles sacrées. 
                    Que l'Esprit Saint guide notre union et nous aide à appliquer la Parole de Dieu dans notre vie 
                    matrimoniale et familiale.
                  </p>
                  <div className="bg-white/10 rounded-xl p-4 border-l-4 border-yellow-400">
                    <p className="text-yellow-200 italic font-medium">
                      "Que ces lectures nourrissent notre amour mutuel et renforcent les liens sacrés 
                      qui nous unissent dans le Christ."
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button 
                    onClick={() => window.open('https://lectiodivina.catholique.fr/conseils-pratiques-lectio/conditions-vivre-lectio-divina-mise-en-route/', '_blank')}
                    className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-all duration-300 group"
                  >
                    <span className="text-xl group-hover:scale-110 transition-transform">🙏</span>
                    <span className="font-medium">Bien vivre la lectio divina</span>
                  </button>
                  <button 
                    onClick={() => window.open('https://fr.aleteia.org/2023/09/13/le-devoir-de-sasseoir-le-rendez-vous-qui-fait-grandir-son-couple/', '_blank')}
                    className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-xl transition-all duration-300 group"
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform">👫</span>
                    <span className="font-medium text-sm">Devoir de s'asseoir</span>
                  </button>
                  <button 
                    onClick={() => window.open('https://vivre-et-aimer.org/', '_blank')}
                    className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-xl transition-all duration-300 group"
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform">📚</span>
                    <span className="font-medium text-sm">Vivre et aimer</span>
                  </button>
                  <div className="text-fsf-light text-sm ml-auto">
                    ⏱️ 5-10 minutes
                  </div>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 opacity-30">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                </div>
              </div>
            </div>

            {/* Section intentions de prière */}
            <div className="bg-gradient-to-br text-justify from-rose-50 via-pink-50 to-red-50 rounded-3xl shadow-2xl p-8 border-2 border-red-200/50">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl">🙏</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-fsf-primary font-serif">Intentions de prière</h3>
                  <p className="text-fsf-marriage text-sm">Prions ensemble pour notre communauté</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white/70 rounded-2xl p-4 border border-blue-200/50 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1">👨‍👩‍👧‍👦</span>
                    <div>
                      <h4 className="font-bold text-fsf-primary mb-1">Couples FSF</h4>
                      <p className="text-sm text-gray-700">Pour l'unité de tous nos couples dans le Christ</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 rounded-2xl p-4 border border-yellow-200/50 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1">💒</span>
                    <div>
                      <h4 className="font-bold text-fsf-accent mb-1">Futurs mariés</h4>
                      <p className="text-sm text-gray-700">Pour les couples en préparation au mariage</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 rounded-2xl p-4 border border-red-200/50 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1">💔</span>
                    <div>
                      <h4 className="font-bold text-fsf-marriage mb-1">Couples en difficulté</h4>
                      <p className="text-sm text-gray-700">Pour la réconciliation et la paix</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/70 rounded-2xl p-4 border border-green-200/50 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1">👶</span>
                    <div>
                      <h4 className="font-bold text-green-700 mb-1">Nos enfants</h4>
                      <p className="text-sm text-gray-700">Pour leur éducation chrétienne</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button 
                  onClick={() => window.open('https://hozana.org/priere', '_blank')}
                  className="bg-gradient-to-r from-red-700  to-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 group"
                >
                  <span className="group-hover:scale-110 transition-transform inline-block">🙏</span> Guide de prières inspirantes
                </button>
                <button 
                  onClick={() => window.open('https://www.vaticannews.va/fr/priere/rosaire.html', '_blank')}
                  className="bg-gradient-to-r from-red-600 to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 group"
                >
                  <span className="group-hover:scale-110 transition-transform inline-block">📿</span> Prier le chapelet
                </button>
              </div>
            </div>

            {/* Outils pour couples chrétiens */}
            <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-2 border-amber-200/50">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl">⚒️</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-amber-700 font-serif">Outils pratiques</h3>
                  <p className="text-amber-600 text-sm">Pour approfondir votre vie de couple chrétien</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <button 
                  onClick={() => window.open('https://www.cef.fr/actualites/preparation-au-mariage', '_blank')}
                  className="bg-white/70 rounded-2xl p-4 border border-amber-200/50 hover:shadow-lg transition-all duration-300 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1 group-hover:scale-110 transition-transform">💒</span>
                    <div>
                      <h4 className="font-bold text-amber-700 mb-1">Préparation au mariage</h4>
                      <p className="text-sm text-gray-700">Guides et conseils pour futurs époux</p>
                    </div>
                  </div>
                </button>
                <button 
                  onClick={() => window.open('https://www.famillechretienne.fr/couple-famille', '_blank')}
                  className="bg-white/70 rounded-2xl p-4 border border-amber-200/50 hover:shadow-lg transition-all duration-300 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1 group-hover:scale-110 transition-transform">👨‍👩‍👧‍👦</span>
                    <div>
                      <h4 className="font-bold text-amber-700 mb-1">Vie de famille</h4>
                      <p className="text-sm text-gray-700">Articles sur la famille chrétienne</p>
                    </div>
                  </div>
                </button>
                <button 
                  onClick={() => window.open('https://www.ktotv.com/page/couple-et-famille', '_blank')}
                  className="bg-white/70 rounded-2xl p-4 border border-amber-200/50 hover:shadow-lg transition-all duration-300 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1 group-hover:scale-110 transition-transform">📺</span>
                    <div>
                      <h4 className="font-bold text-amber-700 mb-1">Émissions couples</h4>
                      <p className="text-sm text-gray-700">Vidéos et témoignages</p>
                    </div>
                  </div>
                </button>
                <button 
                  onClick={() => window.open('https://www.bioforce.org/formation-couples', '_blank')}
                  className="bg-white/70 rounded-2xl p-4 border border-amber-200/50 hover:shadow-lg transition-all duration-300 text-left group"
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl mt-1 group-hover:scale-110 transition-transform">📖</span>
                    <div>
                      <h4 className="font-bold text-amber-700 mb-1">Formation couples</h4>
                      <p className="text-sm text-gray-700">Sessions et retraites</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="bg-white/50 rounded-2xl p-4 border border-amber-300/30">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-xl">🎯</span>
                  <h4 className="font-bold text-amber-700">Ressources spéciales Bénin</h4>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <button 
                    onClick={() => window.open('https://eglisecatholiqueaubenin.org/', '_blank')}
                    className="flex items-center justify-between p-3 bg-white/70 rounded-lg hover:bg-white transition-all duration-300 group"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">⛪</span>
                      <span className="font-medium text-amber-700 text-sm">Église Catholique Bénin</span>
                    </div>
                    <span className="text-amber-500 group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar droite - Widget YouTube Lumen Christi TV */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white rounded-3xl shadow-2xl overflow-hidden relative sticky top-8">
              <div className="absolute inset-0 bg-black/20"></div>
              
              <div className="relative z-10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <span className="text-red-200 text-xl">📺</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold font-serif flex items-center gap-2">
                        Lumen Christi TV
                        {latestVideo?.isToday && (
                          <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            🔴 AUJOURD'HUI
                          </span>
                        )}
                        {latestVideo?.isDirect && !latestVideo?.isToday && (
                          <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            📺 DIRECT
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <p className="text-red-200 text-sm">Télévision Catholique Bénin</p>
                        <div className="flex items-center space-x-1">
                          {videoUpdateStatus === 'checking' && (
                            <span className="text-xs text-yellow-300 animate-pulse">🔄 Vérification...</span>
                          )}
                          {videoUpdateStatus === 'updated' && (
                            <span className="text-xs text-green-300">✅ Aujourd'hui</span>
                          )}
                          {videoUpdateStatus === 'recent' && (
                            <span className="text-xs text-blue-300">📺 Récente</span>
                          )}
                          {videoUpdateStatus === 'cached' && (
                            <span className="text-xs text-blue-300">💾 Cache</span>
                          )}
                          {videoUpdateStatus === 'error' && (
                            <span className="text-xs text-red-300">⚠️ Erreur</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Boutons d'action (Intégration de refreshVideoNow) */}
                  <div className="flex space-x-1">
                    <button
                      onClick={refreshVideoNow}
                      disabled={refreshingVideo}
                      className={`p-2 rounded-lg transition-all duration-300 ${
                        refreshingVideo 
                          ? 'bg-white/10 cursor-not-allowed' 
                          : 'bg-white/20 hover:bg-white/30'
                      }`}
                      title="Vérifier les nouvelles vidéos"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingVideo ? 'animate-spin' : ''}`} />
                    </button>
                    
                    <button
                      onClick={clearCacheAndRefresh}
                      className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-all duration-300"
                      title="Changer de vidéo aléatoirement"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Widget YouTube Lumen Christi TV - Affichage du contenu */}
                {videoLoading && !latestVideo ? (
                  <div className="bg-black/30 rounded-xl overflow-hidden mb-4 relative">
                    <div className="aspect-video relative bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-2"></div>
                        <p className="text-sm">Chargement depuis YouTube...</p>
                        <p className="text-xs text-red-200 mt-1">{YOUTUBE_CONFIG.channelHandle}</p>
                      </div>
                    </div>
                  </div>
                ) : latestVideo ? (
                  <div className="bg-black/30 rounded-xl overflow-hidden mb-4 relative">
                    {latestVideo.isChannelRedirect || !latestVideo.embedUrl ? (
                      /* Redirection vers la chaîne si embed impossible */
                      <div 
                        className="aspect-video relative cursor-pointer group bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center"
                        onClick={() => window.open(latestVideo.url, '_blank')}
                      >
                        <div className="text-center text-white relative z-10 p-6">
                          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                            <Globe className="w-10 h-10" />
                          </div>
                          <h4 className="font-bold text-lg mb-2">{latestVideo.title}</h4>
                          <p className="text-red-200 text-sm mb-3">🔗 Cliquer pour accéder à la chaîne YouTube</p>
                        </div>
                        <div className="absolute top-3 right-3">
                          <span className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-bold">
                            📺 YouTube
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* Lecteur YouTube intégré */
                      <div className="aspect-video relative bg-black rounded-xl overflow-hidden">
                        <iframe
                          width="100%"
                          height="100%"
                          src={latestVideo.embedUrl}
                          title={latestVideo.title}
                          frameBorder="0"
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="w-full h-full"
                          loading="lazy"
                          referrerPolicy="strict-origin-when-cross-origin"
                          sandbox="allow-scripts allow-same-origin allow-presentation"
                        ></iframe>
                        <div className="absolute top-3 right-3 flex space-x-2 z-20">
                          <button
                            onClick={() => window.open(latestVideo.url, '_blank')}
                            className="bg-red-600/90 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-bold transition-all duration-300 backdrop-blur-sm"
                            title="Ouvrir sur YouTube"
                          >
                            📺 YouTube
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Métadonnées vidéo */}
                    <div className="p-4 bg-gradient-to-r from-red-900/20 to-black/40">
                      <h4 className="text-white font-bold text-sm mb-2 line-clamp-2">
                        {latestVideo.title}
                      </h4>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <p className="text-red-200 text-xs">📅 {latestVideo.publishedAt}</p>
                          <p className="text-red-300 text-xs">📋 {latestVideo.author}</p>
                        </div>
                        <span className="text-green-400 text-xs font-bold animate-pulse">🔴 DISPONIBLE</span>
                      </div>
                      
                      {/* LOGIQUE D'AFFICHAGE "VIDÉO SÉLECTIONNÉE ALÉATOIREMENT" */}
                      <div className="flex items-center justify-between text-xs mt-1">
                        <div className="flex items-center space-x-2">
                          {latestVideo.isRandom ? (
                            <span className="text-purple-300 flex items-center space-x-1">
                              <span>🎲</span> <span>Sélection aléatoire</span>
                            </span>
                          ) : latestVideo.isPossiblyOutdated ? (
                            <span className="text-yellow-300 flex items-center space-x-1">
                              <Clock className="w-3 h-3"/> <span>Ancienne vidéo du jour</span>
                            </span>
                          ) : (
                            <span className="text-green-300 flex items-center space-x-1">
                              <Clock className="w-3 h-3"/> <span>Vidéo du jour actuelle</span>
                            </span>
                          )}
                          {lastVideoCheck && (
                            <span className="text-blue-300">
                              🕒 Chargé: {lastVideoCheck.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        
                        {videoUpdateStatus !== 'updated' && !refreshingVideo && (
                          <button 
                            onClick={refreshVideoNow}
                            className="text-yellow-300 hover:text-yellow-200 transition-colors"
                            title="Nouvelle vérification recommandée"
                          >
                            🎲 Nouvelle vidéo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Fallback si aucune vidéo trouvée */
                  <div className="bg-black/30 rounded-xl overflow-hidden mb-4 relative">
                    <div className="aspect-video relative bg-gradient-to-br from-red-900 to-black flex items-center justify-center">
                      <div 
                        className="text-center text-white cursor-pointer group"
                        onClick={() => window.open('https://www.youtube.com/@lumenchristitvbenin', '_blank')}
                      >
                        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                          <span className="text-2xl">📺</span>
                        </div>
                        <h4 className="font-bold text-sm mb-2">Lumen Christi TV</h4>
                        <p className="text-red-200 text-xs">Cliquez pour accéder à la chaîne</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Boutons d'actions rapides sous le lecteur */}
                <div className="grid grid-cols-1 gap-2 mb-4">
                  <div 
                    className="flex items-center justify-center bg-white/5 rounded-lg p-3 hover:bg-white/10 cursor-pointer transition-all duration-300 border border-white/10 border-dashed"
                    onClick={() => window.open('https://www.youtube.com/@lumenchristitvbenin/videos', '_blank')}
                  >
                    <div className="text-center text-white">
                      <p className="text-sm font-medium mb-1">📺 Voir toutes les émissions</p>
                      <p className="text-xs text-red-200">Chaîne YouTube complète</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3 ">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">📱 Contenu spirituel quotidien</span>
                      <span className="text-xs text-red-200">🔴 14,2K abonnés</span>
                    </div>
                    <div className="text-xs text-red-100 space-y-1 text-left">
                      <p>• Accès direct aux vraies émissions</p>
                      <p>• Programmes éducatifs catholiques</p>
                      <p>• Émissions en direct depuis le Bénin</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => window.open('https://www.youtube.com/@lumenchristitvbenin', '_blank')}
                      className="flex-1 bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 group"
                    >
                      <span className="text-sm group-hover:scale-110 transition-transform">📺</span>
                      <span className="text-sm">Chaîne complète</span>
                    </button>
                    <button 
                      onClick={() => window.open('https://www.youtube.com/@lumenchristitvbenin/live', '_blank')}
                      className="bg-red-500/80 hover:bg-red-600 py-2 px-3 rounded-lg transition-all duration-300 group"
                      title="Voir le live"
                    >
                      <span className="text-sm group-hover:scale-110 transition-transform">🔴</span>
                    </button>
                    <button 
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: 'Lumen Christi TV',
                            text: 'Découvrez Lumen Christi TV, la télévision catholique du Bénin',
                            url: 'https://www.youtube.com/@lumenchristitvbenin'
                          });
                        } else {
                          navigator.clipboard.writeText('https://www.youtube.com/@lumenchristitvbenin');
                          alert('Lien copié dans le presse-papiers!');
                        }
                      }}
                      className="bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg transition-all duration-300 group"
                      title="Partager"
                    >
                      <span className="text-sm group-hover:scale-110 transition-transform">📤</span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="absolute bottom-2 right-2 opacity-30">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-red-300 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-red-300 rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
                  <div className="w-2 h-2 bg-red-300 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyReadings;